'use client';

/**
 * Functions pane — sibling of MCP / OpenAPI panes on the agent settings.
 *
 * Function source lives as a regular `/content` row (real file under
 * `data/content/<uuid>.<ext>`), so editing happens in the standard
 * `/content/[id]` editor. This pane is the control surface around it:
 * declare, peek "used by", detach a binding, and hand off to the owner's
 * OWN personal agent for any new wiring.
 *
 * The cron / HTTP / tool inline forms used to live here directly. They
 * were noisy, duplicated logic the agent already knows, and easy to
 * misuse (e.g. forgetting auth=signature on a public webhook). Instead,
 * we open a chat with the personal agent and ask it to configure the
 * binding for us — it can read the function's frontmatter, suggest a
 * sensible default, and confirm with the owner before saving.
 *
 * The hand-off used to address @robutler.factory. That agent is retired
 * (2026-09-05): the personal agent carries `build_apps` / `build_agents`
 * itself, and its handle is resolved through `GET /api/agents/personal`
 * (`fetchPersonalAgent`) BEFORE the chat is created, never reconstructed
 * from the owner's username, so a rename or a lookup failure cannot strand
 * a half-made chat.
 */

'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  ChevronDown,
  ChevronRight,
  Clock,
  Code as CodeIcon,
  ExternalLink,
  Link2,
  Pause,
  Play,
  Plus,
  Settings2,
  Trash2,
} from 'lucide-react';
import { useChatSidebar } from '@/components/chat-sidebar-provider';
import { SchedulePicker } from '@/components/schedule-picker';
import { useT } from '@/components/i18n-provider';
import { fetchPersonalAgent } from '@/hooks/use-personal-agent';
import {
  humanizeStructuredSchedule,
  type StructuredSchedule,
} from '@/lib/agents/schedule-helpers';

export interface FunctionsPaneProps {
  agentId: string;
  agentUsername: string;
  /** When false the entire pane renders an upgrade-or-enable nudge. */
  enabled: boolean;
  /** When true, owner can opt in to host-self-edit. */
  selfEditAvailable?: boolean;
}

interface FunctionRow {
  name: string;
  declaration: {
    contentId?: string;
    description?: string;
    runtime?: string;
    permissions?: { secrets?: string[] };
  };
  usedBy: Array<{
    skill: string;
    entryId: string;
    description?: string;
    enabled?: boolean;
    stats?: {
      runs: number;
      failures: number;
      lastRunAt?: string;
      lastStatus?: string;
    };
  }>;
}

interface ListResponse {
  functions: FunctionRow[];
  featureFlags?: { functions?: boolean; selfEdit?: boolean };
}

const DEFAULT_JS_SCAFFOLD = (name: string) => `/**
 * @robutler-function
 * runtime: js-v1
 * entrypoint: handler
 */

// ${name}
//
// This function runs in a sandboxed v8 isolate. The host \`ctx\` exposes
// fetch (allowlisted), secrets, kv, content, folders, log, portal, fn.
// See https://docs.robutler.ai/functions for the full host API.

export default async function handler(ctx) {
  return { ok: true };
}
`;

export function FunctionsPane({ agentId, agentUsername, enabled, selfEditAvailable }: FunctionsPaneProps) {
  const t = useT();
  const router = useRouter();
  const [list, setList] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const refresh = async () => {
    const r = await fetch(`/api/agents/${agentId}/functions`, { cache: 'no-store' });
    const data = (await r.json()) as ListResponse;
    setList(data);
  };

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`/api/agents/${agentId}/functions`, { cache: 'no-store' });
        const data = (await r.json()) as ListResponse;
        if (!cancel) setList(data);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [agentId]);

  if (!enabled) {
    return (
      <div className="text-sm text-muted-foreground">
        {t('agentTools.functionsDisabled')}
      </div>
    );
  }

  if (loading && !list) {
    return <div className="text-sm text-muted-foreground">Loading…</div>;
  }

  const functions = list?.functions ?? [];

  const declare = async () => {
    const name = draftName.trim();
    if (!name) return;
    setCreating(true);
    setCreateError(null);
    try {
      const r = await fetch(`/api/agents/${agentId}/functions`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          name,
          manifest: { runtime: 'js-v1', entrypoint: 'handler' },
          code: DEFAULT_JS_SCAFFOLD(name),
        }),
      });
      if (!r.ok) {
        const d = (await r.json().catch(() => ({}))) as {
          error?: string;
          errors?: Array<{ message: string }>;
        };
        setCreateError(d.error ?? d.errors?.[0]?.message ?? `failed (${r.status})`);
        return;
      }
      const d = (await r.json()) as { declaration?: { contentId?: string } };
      const contentId = d.declaration?.contentId;
      setDraftName('');
      setShowNew(false);
      await refresh();
      if (contentId) router.push(`/content/${contentId}`);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-3">
      {selfEditAvailable && (
        <SelfEditToggle agentId={agentId} initial={list?.featureFlags?.selfEdit ?? false} />
      )}

      <p className="text-sm text-muted-foreground">
        {t('agentTools.functionsIntro')}
      </p>

      <div className="space-y-2">
        {functions.map((f) => (
          <FunctionRow
            key={f.name}
            agentId={agentId}
            agentUsername={agentUsername}
            row={f}
            expanded={expanded === f.name}
            onToggle={() => setExpanded(expanded === f.name ? null : f.name)}
            onChanged={refresh}
          />
        ))}
      </div>

      {showNew ? (
        <form
          className="flex gap-2 items-start"
          onSubmit={(e) => {
            e.preventDefault();
            void declare();
          }}
        >
          <input
            className="flex-1 border border-border rounded-lg px-3 py-2 text-sm font-mono bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
            placeholder={t('agentTools.functionNamePlaceholder')}
            value={draftName}
            onChange={(e) => setDraftName(e.target.value.replace(/[^a-z0-9_]/gi, '_').toLowerCase())}
            disabled={creating}
            maxLength={64}
            autoFocus
          />
          <button
            type="submit"
            className="text-sm px-3 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 shrink-0"
            disabled={creating || !draftName.trim()}
          >
            {creating ? t('agentTools.creating') : t('actions.create')}
          </button>
          <button
            type="button"
            className="text-sm px-3 py-2 rounded-lg border border-border hover:bg-muted/40 shrink-0"
            onClick={() => {
              setShowNew(false);
              setDraftName('');
              setCreateError(null);
            }}
          >
            Cancel
          </button>
        </form>
      ) : (
        <button
          type="button"
          className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-border hover:bg-muted/40"
          onClick={() => setShowNew(true)}
        >
          <Plus className="h-4 w-4" />
          {t('agentTools.declareFunction')}
        </button>
      )}
      {createError && <div className="text-xs text-destructive">{createError}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-function row — collapsible card matching the ToolCard pattern.
// ---------------------------------------------------------------------------

function FunctionRow({
  agentId,
  agentUsername,
  row,
  expanded,
  onToggle,
  onChanged,
}: {
  agentId: string;
  agentUsername: string;
  row: FunctionRow;
  expanded: boolean;
  onToggle: () => void;
  onChanged: () => Promise<void>;
}) {
  const t = useT();
  const router = useRouter();
  const usedSummary =
    row.usedBy.length === 0 ? 'unused' : `used as ${row.usedBy.map((u) => u.skill).join(', ')}`;

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <div
        className="flex items-center justify-between p-3 @md/main:p-4 cursor-pointer hover:bg-muted/30 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3 min-w-0">
          <CodeIcon className="h-5 w-5 text-muted-foreground shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="text-sm font-medium truncate">{row.name}</p>
              {expanded ? (
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
              )}
            </div>
            <p className="text-xs text-muted-foreground truncate">
              {row.declaration.runtime ?? 'js-v1'}
              {row.declaration.description ? ` — ${row.declaration.description}` : ''}
            </p>
          </div>
        </div>
        <span className="text-xs text-muted-foreground shrink-0">{usedSummary}</span>
      </div>

      {expanded && (
        <div className="px-3 @md/main:px-4 pb-3 @md/main:pb-4 pt-3 border-t border-border/50 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-border hover:bg-muted/40 disabled:opacity-50"
              onClick={() => row.declaration.contentId && router.push(`/content/${row.declaration.contentId}`)}
              disabled={!row.declaration.contentId}
            >
              <ExternalLink className="h-3.5 w-3.5" />
              {t('agentTools.openInEditor')}
            </button>
            <ConnectButton
              agentId={agentId}
              agentUsername={agentUsername}
              functionName={row.name}
              contentId={row.declaration.contentId}
              hasBindings={row.usedBy.length > 0}
              onAttached={onChanged}
            />
          </div>

          {row.usedBy.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs text-muted-foreground">Bindings:</p>
              <div className="flex flex-wrap gap-1.5">
                {row.usedBy.map((u) => (
                  <BindingBadge
                    key={u.entryId}
                    agentId={agentId}
                    functionName={row.name}
                    binding={u}
                    onChanged={onChanged}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Connect via the owner's personal agent
// ---------------------------------------------------------------------------

/**
 * Open a DM with the owner's own personal agent pre-filled with a request
 * to wire the function into a binding (cron / HTTP / tool). The agent then
 * drives the actual `add_to_skill` call after confirming with the owner.
 *
 * Two-shape primary CTA:
 *   - No bindings yet -> primary "Connect" button (filled).
 *   - Already bound  -> ghost "Add binding" so the badges remain the
 *                       focal point of the row.
 */
// ---------------------------------------------------------------------------
// Inline cron form
// ---------------------------------------------------------------------------

/**
 * <CronBindingForm> — inline schedule attach for the common case where
 * the owner already knows *when* they want this function to fire. The
 * personal-agent chat path remains the recommended flow for anything HTTP /
 * tool / non-trivial, so the form is intentionally minimal:
 *
 *   - SchedulePicker for the cadence (no raw cron syntax)
 *   - POST /api/agents/[id]/skills/cron/use with `{ use, entry }`
 *
 * The structured value is translated to a UTC cron string at submit
 * time via `cronExpressionFromStructured`. Once §P2
 * structured-schedule-shape lands we'll persist the structured value
 * directly and delete the translator.
 */
function CronBindingForm({
  agentId,
  functionName,
  onAttached,
  onCancel,
}: {
  agentId: string;
  functionName: string;
  onAttached: () => Promise<void>;
  onCancel: () => void;
}) {
  const t = useT();
  const detectedTz = typeof Intl !== 'undefined'
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : 'UTC';
  const [schedule, setSchedule] = useState<StructuredSchedule>({
    frequency: 'daily',
    preferredTime: '09:00',
    preferredDay: 1,
    timeZone: detectedTz,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      // §P2 structured-schedule-shape: write the structured shape
      // directly. The legacy raw-cron field is omitted so new rows are
      // born in the new format. Old rows are migrated server-side by
      // `scripts/migrate-cron-to-structured.ts`.
      const res = await fetch(`/api/agents/${agentId}/skills/cron/use`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          use: functionName,
          entry: {
            schedule,
            description: humanizeStructuredSchedule(schedule),
          },
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        const errs = (d as { errors?: Array<{ message?: string; action?: string }> }).errors;
        const upgrade = errs?.find((e) => e.action === 'upgrade_plan');
        setError(upgrade?.message ?? (d as { error?: string }).error ?? `failed (${res.status})`);
        return;
      }
      await onAttached();
      onCancel();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-border p-3 space-y-3 bg-muted/20">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5" />
          Schedule {functionName}
        </p>
        <p className="text-xs text-muted-foreground">{humanizeStructuredSchedule(schedule)}</p>
      </div>
      <SchedulePicker value={schedule} onChange={setSchedule} />
      {error && <p className="text-xs text-destructive">{error}</p>}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {busy ? t('agentTools.attaching') : t('agentTools.attachSchedule')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-border hover:bg-muted/40 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function ConnectButton({
  agentId,
  agentUsername,
  functionName,
  contentId,
  hasBindings,
  onAttached,
}: {
  agentId: string;
  agentUsername: string;
  functionName: string;
  contentId: string | undefined;
  hasBindings: boolean;
  onAttached: () => Promise<void>;
}) {
  const t = useT();
  const chatSidebar = useChatSidebar();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Inline cron form covers the most common attach path — cron — without
  // a chat round-trip. HTTP / tool bindings still go through the personal
  // agent because they require auth / route / arg-schema decisions the
  // picker can't model.
  const [showCronForm, setShowCronForm] = useState(false);

  const openConnectChat = async () => {
    setBusy(true);
    setError(null);
    try {
      // Resolve the owner's personal agent FIRST: nothing is written until
      // there is a recipient. A guest cannot own an agent, so the guest
      // branch is theoretical here, but the copy is right if it ever fires.
      let recipient: string;
      try {
        const lookup = await fetchPersonalAgent();
        if (!lookup.agent) {
          setError(t(lookup.guest ? 'personalAgent.signUpCta' : 'personalAgent.notReady'));
          return;
        }
        recipient = lookup.agent.username;
      } catch {
        setError(t('personalAgent.openFailed'));
        return;
      }

      const contentRef = contentId ? `\nFrontmatter source: /content/${contentId}` : '';
      const prompt =
        `Please attach my function \`${functionName}\` to @${agentUsername}.\n` +
        `Read its frontmatter, then propose a binding (HTTP route or LLM tool) ` +
        `based on what it looks like the function does. Confirm with me before saving.` +
        contentRef;

      const res = await fetch('/api/messages/create-with-message', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          type: 'dm',
          participantUsernames: [recipient],
          content: prompt,
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError((d as { error?: string }).error ?? `failed (${res.status})`);
        return;
      }
      const d = (await res.json()) as { chatId?: string };
      if (d.chatId) {
        window.dispatchEvent(new CustomEvent('chatCreated', { detail: { chatId: d.chatId } }));
        chatSidebar.open(d.chatId);
      }
    } finally {
      setBusy(false);
    }
  };

  const primary = !hasBindings;

  if (showCronForm) {
    return (
      <CronBindingForm
        agentId={agentId}
        functionName={functionName}
        onAttached={onAttached}
        onCancel={() => setShowCronForm(false)}
      />
    );
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button
        type="button"
        onClick={() => setShowCronForm(true)}
        disabled={busy}
        className={
          primary
            ? 'inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50'
            : 'inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-border hover:bg-muted/40 disabled:opacity-50'
        }
      >
        <Clock className="h-3.5 w-3.5" />
        Schedule
      </button>
      <button
        type="button"
        onClick={openConnectChat}
        disabled={busy}
        className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-border hover:bg-muted/40 disabled:opacity-50"
      >
        <Link2 className="h-3.5 w-3.5" />
        {busy ? t('agentTools.openingChat') : t('agentTools.httpOrTool')}
      </button>
      {error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Binding badges
// ---------------------------------------------------------------------------

const SKILL_LABELS: Record<string, string> = {
  cron: 'cron',
  custom_http: 'HTTP',
  custom_tools: 'tool',
};

function relativeTimeFrom(iso: string | undefined): string | undefined {
  if (!iso) return undefined;
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return undefined;
  const delta = Date.now() - then;
  if (delta < 60_000) return 'just now';
  if (delta < 3_600_000) return `${Math.floor(delta / 60_000)}m ago`;
  if (delta < 86_400_000) return `${Math.floor(delta / 3_600_000)}h ago`;
  return `${Math.floor(delta / 86_400_000)}d ago`;
}

function BindingBadge({
  agentId,
  functionName,
  binding,
  onChanged,
}: {
  agentId: string;
  functionName: string;
  binding: {
    skill: string;
    entryId: string;
    description?: string;
    enabled?: boolean;
    stats?: { runs: number; failures: number; lastRunAt?: string; lastStatus?: string };
  };
  onChanged: () => Promise<void>;
}) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [runHint, setRunHint] = useState<string | null>(null);
  const label = SKILL_LABELS[binding.skill] ?? binding.skill;
  const isCron = binding.skill === 'cron';

  const detach = async () => {
    setBusy(true);
    try {
      await fetch(`/api/agents/${agentId}/skills/${binding.skill}/use/${binding.entryId}`, {
        method: 'DELETE',
      });
      await onChanged();
    } finally {
      setBusy(false);
    }
  };

  const togglePause = async () => {
    setBusy(true);
    try {
      const next = binding.enabled === false; // resume if currently paused
      const res = await fetch(
        `/api/agents/${agentId}/skills/cron/use/${binding.entryId}`,
        {
          method: 'PATCH',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ enabled: next }),
        },
      );
      if (res.ok) await onChanged();
    } finally {
      setBusy(false);
    }
  };

  // Run-now: fires the function through the manual-invoke route so the
  // execution path is byte-identical to a real cron tick (same executor,
  // same metering, same audit row). Used by the owner to verify a fresh
  // schedule without waiting for the next minute boundary.
  const runNow = async () => {
    setBusy(true);
    setRunHint(null);
    try {
      const res = await fetch(
        `/api/agents/${agentId}/functions/${functionName}/invoke`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({}),
        },
      );
      const d = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        errorMessage?: string;
      };
      setRunHint(res.ok && d.ok ? 'ran ok' : d.errorMessage ?? `failed (${res.status})`);
      // Refresh stats — last-run-at + runs/failures will pick up the
      // new row.
      await onChanged();
    } catch (e) {
      setRunHint((e as Error).message);
    } finally {
      setBusy(false);
      window.setTimeout(() => setRunHint(null), 4000);
    }
  };
  // HTTP endpoints + LLM tools stay monospace (they're literal code-ish
  // identifiers — paths, tool names). Cron rendering is plain prose ("daily
  // 9 AM UTC") and must never show 5-field syntax to the owner.
  const descClass = binding.skill === 'cron' ? '' : 'font-mono';
  const stats = binding.stats;
  const hasFailures = (stats?.failures ?? 0) > 0;
  // Roll-up: "23 runs · 0 failed · 12m ago" rendered next to the
  // description so the owner can see binding health at a glance without
  // opening Activity. Failures are color-coded — red if the binding is
  // currently failing, muted if the rate is clean.
  const summary = stats
    ? [
        stats.runs > 0 ? `${stats.runs} run${stats.runs === 1 ? '' : 's'}` : null,
        hasFailures ? `${stats.failures} failed` : null,
        relativeTimeFrom(stats.lastRunAt),
      ]
        .filter(Boolean)
        .join(' · ')
    : null;

  if (isCron && editing) {
    return (
      <CronEditForm
        agentId={agentId}
        entryId={binding.entryId}
        onSaved={async () => {
          setEditing(false);
          await onChanged();
        }}
        onCancel={() => setEditing(false)}
      />
    );
  }

  return (
    <span
      className={
        'inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md bg-muted/40 border ' +
        (binding.enabled === false ? 'border-amber-400/40 opacity-60' : 'border-border/50')
      }
      title={`${binding.skill} · ${binding.entryId}${stats?.lastRunAt ? ` · last run ${stats.lastRunAt}` : ''}`}
    >
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
      {binding.description && <span className={descClass}>{binding.description}</span>}
      {summary && (
        <span
          className={
            'text-[10px] ' +
            (hasFailures ? 'text-destructive' : 'text-muted-foreground')
          }
        >
          {summary}
        </span>
      )}
      {runHint && (
        <span className="text-[10px] text-muted-foreground italic">{runHint}</span>
      )}
      {isCron && (
        <>
          <button
            type="button"
            onClick={runNow}
            disabled={busy}
            className="p-0.5 rounded hover:bg-primary/15 text-muted-foreground hover:text-primary transition-colors disabled:opacity-50"
            aria-label={t('agentTools.runNow')}
            title={t('agentTools.runNow')}
          >
            <Play className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={togglePause}
            disabled={busy}
            className="p-0.5 rounded hover:bg-amber-400/15 text-muted-foreground hover:text-amber-600 transition-colors disabled:opacity-50"
            aria-label={binding.enabled === false ? t('agentTools.resume') : t('agentTools.pause')}
            title={binding.enabled === false ? t('agentTools.resume') : t('agentTools.pause')}
          >
            {binding.enabled === false ? <Play className="h-3 w-3" /> : <Pause className="h-3 w-3" />}
          </button>
          <button
            type="button"
            onClick={() => setEditing(true)}
            disabled={busy}
            className="p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            aria-label={t('agentTools.editSchedule')}
            title={t('agentTools.editSchedule')}
          >
            <Settings2 className="h-3 w-3" />
          </button>
        </>
      )}
      <button
        type="button"
        onClick={detach}
        disabled={busy}
        className="p-0.5 rounded hover:bg-destructive/15 text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
        aria-label={t('agentTools.detachBinding')}
      >
        <Trash2 className="h-3 w-3" />
      </button>
    </span>
  );
}

/**
 * <CronEditForm> — inline schedule edit for an EXISTING cron entry.
 * Reuses the same SchedulePicker as the create flow; on save it issues
 * a PATCH to swap `schedule` + `description` without going through the
 * full add/remove dance (and thus without bumping the schedule-cap
 * accounting). The form starts blank — we don't know the entry's
 * current schedule from the badge data — so the owner picks the new
 * cadence fresh. Cancel reverts without writing.
 */
function CronEditForm({
  agentId,
  entryId,
  onSaved,
  onCancel,
}: {
  agentId: string;
  entryId: string;
  onSaved: () => Promise<void>;
  onCancel: () => void;
}) {
  const t = useT();
  const detectedTz = typeof Intl !== 'undefined'
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : 'UTC';
  const [schedule, setSchedule] = useState<StructuredSchedule>({
    frequency: 'daily',
    preferredTime: '09:00',
    preferredDay: 1,
    timeZone: detectedTz,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/agents/${agentId}/skills/cron/use/${entryId}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          schedule,
          description: humanizeStructuredSchedule(schedule),
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError((d as { error?: string }).error ?? `failed (${res.status})`);
        return;
      }
      await onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-border p-3 space-y-3 bg-muted/20 w-full">
      <p className="text-xs font-medium flex items-center gap-1.5">
        <Settings2 className="h-3.5 w-3.5" />
        {t('agentTools.editSchedule')}
        <span className="text-muted-foreground">· {humanizeStructuredSchedule(schedule)}</span>
      </p>
      <SchedulePicker value={schedule} onChange={setSchedule} />
      {error && <p className="text-xs text-destructive">{error}</p>}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {busy ? t('posts.savingChanges') : t('actions.save')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-border hover:bg-muted/40 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Self-edit toggle
// ---------------------------------------------------------------------------

function SelfEditToggle({ agentId, initial }: { agentId: string; initial: boolean }) {
  const t = useT();
  const [on, setOn] = useState(initial);
  const [saving, setSaving] = useState(false);

  return (
    <label className="flex items-center gap-2 text-sm border border-border rounded-lg p-3 cursor-pointer hover:bg-muted/30 transition-colors">
      <input
        type="checkbox"
        checked={on}
        disabled={saving}
        onChange={async (e) => {
          const next = e.target.checked;
          setSaving(true);
          try {
            await fetch(`/api/agents/${agentId}/feature-flags`, {
              method: 'POST',
              headers: { 'content-type': 'application/json' },
              body: JSON.stringify({ selfEdit: next }),
            });
            setOn(next);
          } finally {
            setSaving(false);
          }
        }}
      />
      <span>
        <span className="font-medium">{t('agents.selfUpgradeAllowAgent')}</span>
        <span className="text-muted-foreground">{t('agentTools.selfUpgradeHint')}</span>
      </span>
    </label>
  );
}
